import { Input } from '@verlo/shared/components/ui/input';
import { origin } from '../components/BackBar';
import { useState, useEffect, useRef, useCallback } from 'react';
import { menuApi, ordersApi, paymentsApi } from '@verlo/shared/services/api';
import { randomIdempotencyKey } from '@verlo/shared/lib/idempotency';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { Button } from '@verlo/shared/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { saveOrderDraft, loadOrderDraft, clearOrderDraft } from '../lib/orderDraft';

// One bar tab is built at a time per terminal, so a single draft slot.
const BAR_DRAFT_KEY = 'bar';

// Reuse MenuSection from order components
import { MenuSection } from '../components/order';
import { SizeSelectDialog, ItemDetailDialog } from '../components/order';

// Bar-specific components
import { BarPaymentDialog, BarItemEditDialog } from '../components/bar';
import { sheetInitialFor } from '../lib/sheetEdit';
import { addOrMore } from '../lib/sentLines';
import { repriced } from '../lib/barBill';
import ModifierSheet, { needsSheet, ticketWords } from '../components/order/ModifierSheet';
import { OrderPanel } from '../components/order';
import { asksOnTap } from '@verlo/shared/lib/tapChoice';
import useVatRate, { vatInside } from '../hooks/useVatRate';
import { sizedName } from '@verlo/shared/lib/sizeChoice';

// Drink sub-categories to exclude from kids
const kidsSubCategories = ['kids_starters', 'kids_mains', 'kids_drinks', 'kids_desserts'];

const BarOrdersPage = () => {
  const navigate = useNavigate();
  // Core state
  const [menuItems, setMenuItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [sheetItem, setSheetItem] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetInitial, setSheetInitial] = useState(null);     // a cart line being edited
  const [sheetEditIndex, setSheetEditIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [customerName, setCustomerName] = useState('');
  // The server's figures for the order being paid, when they differ from the cart's (barBill.js).
  const [billed, setBilled] = useState(null);
  
  // Payment state
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [paymentStep, setPaymentStep] = useState('waiting');
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' | 'cash'
  const payingRef = useRef(false);
  const paymentKeyRef = useRef(null);
  // The order this payment attempt created, kept across a retry so a payment failure after
  // the order was created doesn't fire a second kitchen ticket (see `takePayment`).
  const createdOrderRef = useRef(null);
  
  // Size selection state
  const [sizeSelectOpen, setSizeSelectOpen] = useState(false);
  const [itemForSize, setItemForSize] = useState(null);
  
  // Item edit state
  const [itemEditOpen, setItemEditOpen] = useState(false);
  const [editingCartIndex, setEditingCartIndex] = useState(null);
  const [itemModifiers, setItemModifiers] = useState([]);
  const [itemNotes, setItemNotes] = useState('');
  const [customModifier, setCustomModifier] = useState('');

  // Item detail (long-press) state — allergens / ingredients preview
  const [itemDetailOpen, setItemDetailOpen] = useState(false);
  const [selectedDetailItem, setSelectedDetailItem] = useState(null);

  // Fetch menu
  useEffect(() => {
    const fetchMenu = async () => {
      setLoading(true);
      try {
        const res = await menuApi.getAll();
        // Filter out kids items for bar
        const barItems = res.data.filter(item => !kidsSubCategories.includes(item.category));
        setMenuItems(barItems);
      } catch (err) {
        toast.error('Failed to load menu');
      } finally {
        setLoading(false);
      }
    };
    fetchMenu();
  }, []);

  // ---- Draft tab: survive a lock (manual or idle) ----
  const draftRestored = useRef(false);
  useEffect(() => {
    if (loading || draftRestored.current) return;
    draftRestored.current = true;
    const draft = loadOrderDraft(BAR_DRAFT_KEY);
    if (draft && draft.length) {
      setCart(draft);
      toast.info('Restored your bar tab');
    }
  }, [loading]);

  useEffect(() => {
    if (loading) return;
    if (cart.length) saveOrderDraft(BAR_DRAFT_KEY, cart);
    else clearOrderDraft(BAR_DRAFT_KEY);
  }, [cart, loading]);

  // Currency formatter
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
  }, []);

  // Calculations
  const subtotal = cart.reduce((sum, item) => sum + (item.subtotal || 0), 0);
  // The venue's rate, not 20% written in (Chris's review H3).
  const vatRate = useVatRate();
  const vatAmount = vatInside(subtotal, vatRate);

  // === CART HANDLERS ===
  
  const handleMenuItemClick = useCallback((item) => {
    // Off right now (hours plan) — the bar has hours too: a cocktail list that starts at
    // five, a brunch spritz that stops at two.
    if (item.available_now === false) {
      toast.error(item.hours ? `${item.name} is on ${item.hours}` : `${item.name} is not on right now`);
      return;
    }
    if (asksOnTap(item)) {
      // "Always ask" (modifiers plan): a Bloody Mary opens the sheet on every tap; so does a
      // choice that changes the price, a pint or a half (lib/tapChoice).
      setSheetItem(item); setSheetInitial(null); setSheetEditIndex(null); setSheetOpen(true);
    } else if (item.has_sizes && item.size_options?.length > 0) {
      setItemForSize(item);
      setSizeSelectOpen(true);
    } else {
      addToCart(item);
    }
  }, [cart]);

  const addToCart = useCallback((item) => {
    const existingIndex = cart.findIndex(
      c => c.menu_item_id === item.id && (c.modifiers?.length === 0 || !c.modifiers) && !c.size
    );

    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      newCart[existingIndex].subtotal = newCart[existingIndex].price * newCart[existingIndex].quantity;
      setCart(newCart);
    } else {
      setCart([...cart, {
        menu_item_id: item.id,
        name: item.name,
        price: item.price,
        quantity: 1,
        modifiers: [],
        notes: '',
        subtotal: item.price,
      }]);
    }
    toast.success(`Added ${item.name}`);
  }, [cart]);

  const addToCartWithSize = useCallback((item, sizeOption) => {
    const finalPrice = sizeOption.price !== undefined ? sizeOption.price : item.price + (sizeOption.price_modifier || 0);
    const itemName = `${item.name} (${sizeOption.name})`;
    
    setCart([...cart, {
      menu_item_id: item.id,
      name: itemName,
      price: finalPrice,
      quantity: 1,
      modifiers: [],
      notes: '',
      subtotal: finalPrice,
      size: sizeOption.name,
      category: item.category,
    }]);
    toast.success(`Added ${itemName}`);
    setSizeSelectOpen(false);
    setItemForSize(null);
  }, [cart]);

  const updateQuantity = useCallback((index, delta) => {
    const newCart = [...cart];
    newCart[index].quantity += delta;
    if (newCart[index].quantity <= 0) {
      newCart.splice(index, 1);
    } else {
      newCart[index].subtotal = newCart[index].price * newCart[index].quantity;
    }
    setCart(newCart);
  }, [cart]);

  const removeFromCart = useCallback((index) => {
    setCart(cart.filter((_, i) => i !== index));
  }, [cart]);

  const clearCart = useCallback(() => {
    setCart([]);
    setCustomerName('');
  }, []);

  // === ITEM EDIT HANDLERS ===
  
  const openItemEdit = useCallback((index) => {
    const item = cart[index];
    const menuItem = menuItems.find((m) => m.id === item.menu_item_id);
    if (menuItem && (needsSheet(menuItem) || (item.modifiers || []).some((m) => typeof m === 'object'))) {
      // The whole line, not its choices alone: the sheet sends its note and allergy note
      // back as they stand, so an edit opened without them wiped them.
      setSheetItem(menuItem); setSheetInitial(sheetInitialFor(item)); setSheetEditIndex(index); setSheetOpen(true);
      return;
    }
    setEditingCartIndex(index);
    setItemModifiers(item.modifiers || []);
    setItemNotes(item.notes || '');
    setCustomModifier('');
    setItemEditOpen(true);
  }, [cart, menuItems]);

  const saveItemModifications = useCallback(() => {
    if (editingCartIndex !== null) {
      const newCart = [...cart];
      newCart[editingCartIndex] = {
        ...newCart[editingCartIndex],
        modifiers: itemModifiers,
        notes: itemNotes,
      };
      setCart(newCart);
      toast.success('Item updated');
    }
    setItemEditOpen(false);
    setEditingCartIndex(null);
    setItemModifiers([]);
    setItemNotes('');
  }, [cart, editingCartIndex, itemModifiers, itemNotes]);

  // === PAYMENT HANDLERS ===
  
  const handleTakePayment = useCallback(() => {
    if (cart.length === 0) {
      toast.error('Add items to the order first');
      return;
    }
    paymentKeyRef.current = null;
    createdOrderRef.current = null;
    setCheckoutOpen(true);
    setPaymentStep('waiting');
  }, [cart]);

  // Persist the bar order, then record the payment against it. Both must succeed for
  // the "complete" screen — there is no simulated success (issue #13). Bar orders have
  // no table_id; the KDS filters drinks out itself, so food on a mixed order still shows.
  const takePayment = useCallback(async (tendered) => {
    if (payingRef.current) return;
    payingRef.current = true;
    setPaymentStep('processing');
    try {
      // Generated inside the try: crypto.randomUUID() is undefined outside a secure context
      // (plain HTTP, which is how the kiosk is normally reached on the LAN), and throwing
      // before this point would leave payingRef stuck true and the dialog stuck on
      // "Recording payment…" with no way to retry.
      if (!paymentKeyRef.current) paymentKeyRef.current = randomIdempotencyKey();

      // A retry after the payment call failed must not re-fire the kitchen ticket — reuse
      // the order this attempt already created rather than creating a second one.
      let order = createdOrderRef.current;
      if (!order) {
        const created = await ordersApi.create({
          table_id: null,
          type: 'bar',
          items: cart.map((c) => ({
            menu_item_id: c.menu_item_id || c.id,
            name: c.name,
            category: c.category,
            price: c.price,
            quantity: c.quantity,
            modifiers: c.modifiers || [],
            notes: c.notes || (customerName ? `Bar · ${customerName}` : 'Bar'),
            allergy_notes: c.allergy_notes || '',
            ...(c.components ? { components: c.components, is_meal: true } : {}),
            subtotal: c.subtotal,
            size: c.size,
          })),
          notes: customerName ? `Bar order: ${customerName}` : 'Bar order',
        });
        order = created.data;
        createdOrderRef.current = order;
        // Priced by the server with today's offers and prices: if that isn't what the screen
        // said, show what's due and ask again before taking anything.
        const bill = repriced(order, subtotal);
        if (bill) {
          setBilled(bill);
          toast.info(`The bill comes to ${formatCurrency(bill.total)} with today's prices. Take that.`);
          setPaymentStep('waiting');
          return;
        }
      }
      await paymentsApi.addPartialPayment(order.id, {
        order_id: order.id,
        amount: order.total,
        method: paymentMethod,
        tip_amount: 0,
        ...(paymentMethod === 'cash' && tendered != null ? { tendered } : {}),
        idempotency_key: paymentKeyRef.current,
      });
      clearOrderDraft(BAR_DRAFT_KEY);
      paymentKeyRef.current = null;
      createdOrderRef.current = null;
      setPaymentStep('success');
    } catch (err) {
      const raw = err.response?.data?.detail;
      // A typed-extra refusal arrives as `{error, message}`, not a string.
      const detail = typeof raw === 'string' ? raw : raw?.message;
      toast.error(detail || 'Payment could not be recorded. Check the card machine and try again.');
      setPaymentStep('waiting');
    } finally {
      payingRef.current = false;
    }
  }, [cart, customerName, paymentMethod, subtotal, formatCurrency]);

  const clearAndStartNew = useCallback(() => {
    clearCart();
    setCheckoutOpen(false);
    setPaymentStep('waiting');
    paymentKeyRef.current = null;
    createdOrderRef.current = null;
    setBilled(null);
  }, [clearCart]);

  // Loading state
  if (loading) {
    return (
      <div className="flex gap-6 h-[calc(100vh-8rem)]">
        <div className="flex-1">
          <Skeleton className="h-10 w-64 mb-4" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-32 rounded-lg" />)}
          </div>
        </div>
        <Skeleton className="w-80 h-full rounded-lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 min-h-[calc(100vh-8rem)] lg:flex-row lg:h-[calc(100vh-7rem)] lg:min-h-0" data-testid="bar-orders-page">
      {/* Menu Section. `min-w-0`: a flex item is otherwise as wide as its widest content, and
          the section tabs (which scroll) made the column ~940px, pushing the order off a
          1024x768 till. */}
      <div className="flex flex-1 min-w-0 flex-col gap-3 lg:min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-heading text-ink">Bar Orders</h1>
            <p className="text-ink-soft">Quick orders without a table number</p>
          </div>
          {/* The bar's own board: every round of drinks sent from any till (F4). */}
          <Button variant="outline" className="h-9" onClick={() => navigate('/bar-board', { state: origin('/bar', 'Bar') })} data-testid="bar-board-btn">
            Bar board
          </Button>
        </div>

        {/* Reuse MenuSection component */}
        <MenuSection
          menuItems={menuItems}
          onItemClick={handleMenuItemClick}
          onItemInfo={(item) => {
            // A long press opens the modifier sheet (allergens and what's in it are on it);
            // a tap still adds instantly — Martin's call, 21 Sep.
            setSheetItem(item); setSheetInitial(null); setSheetEditIndex(null); setSheetOpen(true);
          }}
          formatCurrency={formatCurrency}
          defaultCategory="drinks"
          bar
          fill
        />
      </div>

      {/* The order pad — the same one the order page uses, in bar mode (no table, no send). */}
      <OrderPanel
        fill
        mode="bar"
        cart={cart}
        order={null}
        tickets={[]}
        orderPayments={[]}
        remainingBalance={subtotal}
        subtotal={subtotal}
        vatAmount={vatAmount}
        processing={processing}
        onUpdateQuantity={updateQuantity}
        onRemoveItem={removeFromCart}
        onEditItem={openItemEdit}
        onClearCart={clearCart}
        onCheckout={handleTakePayment}
        formatCurrency={formatCurrency}
        headerExtra={(
          <Input
            placeholder="Customer name (optional)"
            aria-label="Customer name (optional)"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            className="h-9"
            data-testid="customer-name-input"
          />
        )}
      />

      {/* Size Selection Dialog */}
      <ModifierSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        item={sheetItem}
        initial={sheetInitial}
        confirmLabel={sheetEditIndex != null ? 'Update line' : 'Add to order'}
        formatCurrency={formatCurrency}
        onConfirm={(item, mods, modifierTotal, unit, message = {}) => {
          // The price shown is the sheet's mirror of the server's sum; the server re-prices.
          // The sheet's note and allergy note are kept, as on the table page: the bar
          // dropped them, so a note on a dish with choices never reached the kitchen.
          setCart((prev) => {
            if (sheetEditIndex != null && prev[sheetEditIndex]) {
              const next = [...prev];
              const line = {
                ...next[sheetEditIndex], modifiers: mods, price: unit,
                ...(message.size ? { size: message.size, name: sizedName(item.name, message.size) } : {}),
                notes: message.notes ?? next[sheetEditIndex].notes ?? '',
                allergy_notes: message.allergy_notes ?? next[sheetEditIndex].allergy_notes ?? '',
                ...(message.components ? { components: message.components, is_meal: true } : {}),
              };
              line.subtotal = Math.round(unit * line.quantity * 100) / 100;
              next[sheetEditIndex] = line;
              return next;
            }
            const line = {
              menu_item_id: item.id, name: sizedName(item.name, message.size), price: unit, quantity: 1,
              ...(message.size ? { size: message.size } : {}),
              modifiers: mods, notes: message.notes || '', allergy_notes: message.allergy_notes || '',
              subtotal: unit, category: item.category,
              ...(message.components ? { components: message.components, is_meal: true } : {}),
            };
            // The same again, with the same choices, is one more on its line (lib/sentLines).
            return addOrMore(prev, line);
          });
          const words = ticketWords(mods);
          toast.success(`${sheetEditIndex != null ? 'Updated' : 'Added'} ${sizedName(item.name, message.size)}${words.length ? ` · ${words.join(', ')}` : ''}`);
        }}
      />

      <SizeSelectDialog
        open={sizeSelectOpen}
        onOpenChange={setSizeSelectOpen}
        item={itemForSize}
        onSelectSize={addToCartWithSize}
        formatCurrency={formatCurrency}
      />

      {/* Item Detail Dialog (long-press preview with allergens / ingredients / add-with-modifiers) */}
      <ItemDetailDialog
        open={itemDetailOpen}
        onOpenChange={setItemDetailOpen}
        item={selectedDetailItem}
        onAddToOrder={(item, options) => {
          if (options?.modifiers?.length > 0) {
            const selectedSize = options.size || (item.has_sizes && item.size_options?.length === 1 ? item.size_options[0].name : null);
            addToCart({ ...item, selectedModifiers: options.modifiers, selectedSize });
          } else if (options?.size) {
            addToCart({ ...item, selectedSize: options.size });
          } else {
            handleMenuItemClick(item);
          }
        }}
        formatCurrency={formatCurrency}
      />

      {/* Item Edit Dialog */}
      <BarItemEditDialog
        open={itemEditOpen}
        onOpenChange={setItemEditOpen}
        itemName={editingCartIndex !== null ? cart[editingCartIndex]?.name : ''}
        modifiers={itemModifiers}
        notes={itemNotes}
        customModifier={customModifier}
        onModifiersChange={setItemModifiers}
        onNotesChange={setItemNotes}
        onCustomModifierChange={setCustomModifier}
        onSave={saveItemModifications}
      />

      {/* Payment Dialog */}
      <BarPaymentDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        paymentStep={paymentStep}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={setPaymentMethod}
        // What's due: the server's figure once it has priced the order, else the cart's.
        subtotal={billed?.total ?? subtotal}
        vatAmount={billed?.tax ?? vatAmount}
        customerName={customerName}
        // Drinks, not lines: two lagers on one line are two.
        cartLength={cart.reduce((n, l) => n + (Number(l.quantity) || 1), 0)}
        onTakePayment={takePayment}
        onClearAndStartNew={clearAndStartNew}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default BarOrdersPage;
